// White-label brand config — one codebase, multiple branded deployments.
// The active brand is chosen at BUILD time by Vite's mode: `vite build --mode
// actiserve` → MODE='actiserve' → the Actiserve brand. `VITE_BRAND` can override.
// Each deployment also points at its own Firebase project via VITE_FB_* (see
// lib/firebase.js + Docs/DEPLOYMENTS.md). Code is shared; only this profile +
// the Firebase keys differ per deployment.

export const BRANDS = {
  presto: {
    id: 'presto',
    name: 'Presto',
    tag: 'CRM',
    logoSrc: '/presto-logo.svg',
    iconSrc: '/presto-icon.svg',
    // Home-screen / PWA icon — needs an opaque square (iOS shows transparent as black).
    appIconSrc: '/presto-icon.svg',
    manifestSrc: '/manifest.presto.webmanifest',
    // Mobile top bar logo height — the logo is rendered white (filter) on the gold
    // bar, exactly like the desktop .brandbar. 22px matches the desktop default.
    topbarLogoH: 22,
    workspaceLabel: 'Presto B2B sales workspace',
    emailExample: 'you@presto.ph',
    themeColor: '#F3BC3E',
    // Empty → use the Presto design-system defaults already in tokens/colors.css.
    theme: {},
  },
  actiserve: {
    id: 'actiserve',
    name: 'Actiserve',
    tag: 'CRM',
    // Official artwork from the Actiserve logo package (AC0101, "Actiserve Logo
    // Digital", 2023) — self-contained SVGs: horizontal lockup for the bar/login,
    // square icon for the favicon. (Logo hexes are CMYK-derived and differ slightly
    // from the screen palette below, which follows the brand manual's RGB spec.)
    logoSrc: '/actiserve-logo.svg',
    iconSrc: '/actiserve-icon.svg',
    // Home-screen / PWA icon — the mark on an opaque white square.
    appIconSrc: '/actiserve-appicon.svg',
    manifestSrc: '/manifest.actiserve.webmanifest',
    // Mobile top bar: the full horizontal lockup (icon + ACTISERVE) rendered white
    // on the cyan bar (desktop .brandbar treatment), enlarged to ~the most that
    // fits a 56px bar — a literal 3× would overflow it.
    topbarLogoH: 34,
    workspaceLabel: 'Actiserve · Nestlé Professional sales workspace',
    emailExample: 'you@actiserve.ph',
    // Brand colors verbatim from the manual (colors_and_type.css): cyan = primary
    // action, grey = primary text/ink, green = success, red = danger.
    themeColor: '#00AED6',
    theme: {
      '--presto-sunny': '#00AED6', // acti-cyan — brand bar, top bar, active rail, accents, primary-button text
      '--presto-sunny-light': '#E0F5FB', // acti-cyan-tint — active backgrounds, hover rows, chips
      '--presto-sunny-dark': '#007E9C', // acti-cyan-active — borders, icon accents, hover
      '--presto-suit': '#54585A', // acti-grey — primary button bg, headings, dark ink
      '--presto-suit-soft': '#44484A', // grey-700 — secondary dark surfaces
      '--presto-success': '#93C90E', // acti-green
      '--presto-success-bg': '#F1F8DD', // acti-green-tint
      '--presto-error': '#D6001C', // acti-red
      '--presto-error-bg': '#FBE6E8', // acti-red-tint
      '--presto-info': '#00AED6', // acti-cyan
      '--presto-info-bg': '#E0F5FB', // acti-cyan-tint
      '--presto-warning': '#E8A500', // manual's derived warning
      '--presto-warning-bg': '#FBEFD0',
      '--brand-glow': 'rgba(0, 174, 214, 0.16)', // cyan login backdrop
      // Actiserve uses a horizontal lockup (needs height to read) — ~3× Presto's,
      // with the brand bar grown to fit it.
      '--brandbar-logo-h': '60px',
      '--brandbar-h': '80px',
    },
  },
};

export const DEFAULT_BRAND = 'presto';

// Pure + unit-tested: id → brand profile (unknown/empty → the default brand).
export function resolveBrand(id) {
  return BRANDS[id] ?? BRANDS[DEFAULT_BRAND];
}

// The active brand for this build (mode name, or an explicit VITE_BRAND override).
const activeId = (import.meta.env?.VITE_BRAND || import.meta.env?.MODE);
export const BRAND = resolveBrand(activeId);

// Apply the brand at runtime: override the CSS color tokens on :root (semantic
// aliases like --color-brand re-resolve to them), set the document title, the
// PWA theme-color and the favicon. No CSS rebuild needed per brand.
export function applyBrand(brand = BRAND) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  Object.entries(brand.theme || {}).forEach(([k, v]) => root.style.setProperty(k, v));
  document.title = `${brand.name} CRM`;
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta && brand.themeColor) themeMeta.setAttribute('content', brand.themeColor);
  const icon = document.querySelector('link[rel="icon"]');
  const iconHref = brand.iconSrc || brand.logoSrc;
  if (icon && iconHref) icon.setAttribute('href', iconHref);
  // Home-screen (iOS "Add to Home Screen") icon — opaque square per brand.
  const apple = document.querySelector('link[rel="apple-touch-icon"]');
  if (apple && brand.appIconSrc) apple.setAttribute('href', brand.appIconSrc);
  // PWA manifest (name + icons) per brand.
  const manifest = document.querySelector('link[rel="manifest"]');
  if (manifest && brand.manifestSrc) manifest.setAttribute('href', brand.manifestSrc);
}
